import React, { useState, useEffect } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { buildProviderTaxPayload } from '../../utils/providerTax';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // SPRINT-5: i18n
import { useClinicData, LEAD_STATUSES } from '../../context/ClinicDataContext';
import { useClinicScope } from '../../hooks/useClinicScope';
import { useClinicLeads } from '../../hooks/useClinicLeads';
import { useRBAC } from '../../context/RBACContext';
import { useAuth } from '../../context/AuthContext';
import { clearUnifiedAuth } from '../../utils/unifiedAuth';
import { useHandoffNotification } from '../../context/HandoffNotificationContext';
import { SubscriptionProvider, useSubscription } from '../../context/SubscriptionContext'; // Sprint 10: Feature gating
import { ResponsibilityBadge, ResponsibilityIndicator } from '../../components/HandoffUI';
import { AssignmentPanel } from '../../components/AssignmentPanel';
import OfferComposer from '../../components/OfferComposer';
import OfferCommercialFacts from '../../components/OfferCommercialFacts';
import { currencySymbol, formatOfferError, currencyPolicyReasonKey } from '../../utils/currencyPolicy';
// M16B-07B — canonical Treatment Ready visibility (per-treatment-slug)
import TreatmentReadyPanel from '../../components/treatment-ready/TreatmentReadyPanel';
import AddLeadModal from './admin/AddLeadModal'; // SPRINT-3: Import AddLeadModal for Sales Rep autonomy
// BULK-LEAD-IMPORT-V1 — separate action; the single-patient flow above is untouched.
import BulkLeadImport from './admin/BulkLeadImport';
import { MarkAsWonDialog } from '../../components/MarkAsWonDialog'; // LIFECYCLE-V3.2 (Bulk status hidden/deferred)
import { SalesStageBadge, SalesActions } from '../../components/leads/SalesStage'; // SALES-CRM-SIMPLIFICATION
import { GatedButton, TrialFeatureNotice } from '../../components/FeatureGatedWrapper'; // Sprint 10: Feature gating
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox'; // SPRINT 9 Batch 2.1: Multi-select
import { 
  Users, FileText, TrendingUp, Clock, CheckCircle, Send,
  ChevronRight, LogOut, User, Eye, X, AlertCircle, DollarSign,
  MessageCircle, Phone, Mail, Globe, Filter, Search, ChevronLeft, RefreshCw, UserPlus, Archive, Lock, CheckSquare, Sparkles, Building2, FileSpreadsheet, Download
} from 'lucide-react';
import api, { clinicLeadAPI } from '../../utils/api';
import { apiErrorMessage } from '../../utils/apiError';
import { toast as sonnerToast } from 'sonner';
import { Toaster } from '../../components/Toaster';
import SalesRepTaskInbox from './SalesRepTaskInbox';

// Bridge: convert Shadcn-style toast calls to sonner
const toast = (opts) => {
  if (typeof opts === 'string') return sonnerToast(opts);
  const { title, description, variant } = opts || {};
  if (variant === 'destructive') return sonnerToast.error(title || 'Error', { description });
  return sonnerToast.success(title || 'Success', { description });
};

// Status constants — CLOSED_STATUSES still gates lead immutability (multi-select + detail).
const CLOSED_STATUSES = ['CLOSED_WON', 'CLOSED_LOST', 'COMPLETED', 'WON', 'LOST'];
// SALES-CRM-SIMPLIFICATION (Batch A) — the ONLY status axis on this screen is the
// backend human stage. `All` + these five, nothing else.
const HUMAN_STAGES = ['NEW', 'WORKING', 'ON_HOLD', 'WON', 'LOST'];
// Human-facing label for any canonical/legacy lead status — never the raw enum.
const statusLabelI18n = (t, status) => t(`provider.leads.statuses.${status}`, LEAD_STATUSES[status]?.label || String(status || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()));

const SalesRepDashboard = () => {
  const { t, i18n } = useTranslation(); // SPRINT-5: i18n hook
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { 
    clinicUser, 
    logoutClinic, 
    leads: contextLeads, // Subscribe for reactivity signal
  } = useClinicData();
  const { clinicId } = useClinicScope();
  const { isAuthenticated } = useRBAC();
  // PROVIDER-LOGOUT-ISOLATION-01 — canonical logout dependencies.
  const { clearRBAC } = useRBAC();
  const { logout } = useAuth();
  const userRole = localStorage.getItem('treatx_role') || '';
  
  // API-driven lead operations (backend source of truth)
  const {
    createLead: apiCreateLead,
    updateStatus: apiUpdateStatus,
    sendInvite: apiSendInvite,
    fetchLeads: apiFetchLeads,
    leads: apiLeads,
    addNote: apiAddNote,
    listNotes: apiListNotes,
    addMessage: apiAddMessage,
    listMessages: apiListMessages,
    createOffer: apiCreateOffer,
    listOffers: apiListOffers,
    getLatestOffer: apiGetLatestOffer,
    assignLead: apiAssignLead,
  } = useClinicLeads(clinicId);
  
  // Sprint 10: Feature gating - check if bulk operations are available
  // Using try-catch because context might not be available yet
  let canUseBulkOperations = true;
  let currentPackage = null;
  try {
    const subscriptionContext = useSubscription();
    canUseBulkOperations = false; // LIFECYCLE-V3.2 — Bulk Change Status hidden/deferred for launch
    currentPackage = subscriptionContext.currentPackage;
  } catch (e) {
    // Context not available, allow features by default
  }
  
  // Helper to get patient info from lead (backend snake_case fields)
  const getPatientForLead = (lead) => {
    return {
      name: lead?.patient_name || lead?.patientName || 'Unknown',
      email: lead?.patient_email || lead?.patientEmail || '',
      phone: lead?.patient_phone || lead?.patientPhone || '',
      country: lead?.country_of_origin || lead?.patientCountry || 'Unknown',
    };
  };
  
  // SPRINT-3: State for Add Own Patient modal
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  
  // SPRINT 8 BATCH 3: State for audit note input
  const [auditNoteText, setAuditNoteText] = useState('');
  
  // P3-DEBUG: State for debug panel visibility
  const [showDebug] = useState(false);
  
  // P3-REVISION: State for viewing sent offer
  const [viewingOffer, setViewingOffer] = useState(null);
  const [viewingOfferLead, setViewingOfferLead] = useState(null);
  const [offerVersions, setOfferVersions] = useState([]);
  // PROVIDER-OWNED-DRAFT-02 — `currency_verified` on the row is a snapshot of
  // the moment the draft was prepared. Once the actual residence is recorded,
  // the EXISTING backend policy authority is re-read here so the draft becomes
  // issuable without re-keying it. The backend re-runs the same policy on
  // issue, so this only mirrors the authority — it never decides eligibility.
  const [draftPolicy, setDraftPolicy] = useState(null);
  // PATIENT-INVITE-EMAIL-03 — the last invitation outcome for the open lead, so
  // the representative can see the truthful delivery state, copy the canonical
  // acceptance link, or retry THAT SAME pending invitation.
  const [inviteState, setInviteState] = useState(null);

  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadDetail, setShowLeadDetail] = useState(false);
  const [showOfferComposer, setShowOfferComposer] = useState(false);
  // TREATX-BATCH-1.1 — when Revise is clicked we prefetch the lead's
  // existing offers so the composer opens in revision mode (`isRevision`)
  // and forwards `parentOfferId = latest.id`. Without this state the
  // composer emits parent_offer_id=null and composer-created v2 remains
  // unlinked from the revision chain.
  const [existingOfferForCompose, setExistingOfferForCompose] = useState(null);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [stats, setStats] = useState({});
  
  // Sprint 7 Batch 3: Sorting state for leads table
  const [leadSort, setLeadSort] = useState({ column: 'createdAt', direction: 'desc' });
  
  // SPRINT 9 Batch 2.1: Multi-select state
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  
  // SPRINT 9 Batch 2.3: Bulk status modal state
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [markWonLead, setMarkWonLead] = useState(null); // LIFECYCLE-V3.2

  // Load leads from backend API — single source of truth
  // P0-3 FIX: Backend now returns only assigned leads for sales_rep role.
  // No dual data source. Counters and list derive from the SAME dataset.
  const loadData = React.useCallback(() => {
    if (!clinicUser || !clinicId) return;
    apiFetchLeads();
  }, [clinicUser, clinicId, apiFetchLeads]);

  // P0-3 FIX: Sync API leads directly — no client-side filtering, no quoteRequests mixing.
  // Backend handles role-based scoping (sales_rep sees only their assigned leads).
  useEffect(() => {
    if (!clinicUser) return;
    const backendLeads = apiLeads || [];
    setLeads(backendLeads);

    // Calculate stats from the SAME dataset that populates the list
    const byStatus = {};
    backendLeads.forEach(l => { byStatus[l.status] = (byStatus[l.status] || 0) + 1; });

    const total = backendLeads.length;
    setStats({
      total,
      totalLeads: total,
      byStatus,
      offersSent: (byStatus['OFFER_SENT'] || 0) + (byStatus['QUOTE_SENT'] || 0),
      newLeads: (byStatus['NEW'] || 0) + (byStatus['ASSIGNED'] || 0),
      conversion: total > 0 ? ((byStatus['WON'] || 0) + (byStatus['PAID'] || 0) + (byStatus['COMPLETED'] || 0) + (byStatus['DEPOSIT_PAID'] || 0) + (byStatus['CLOSED_WON'] || 0)) / total * 100 : 0,
    });
  }, [apiLeads, clinicUser]);

  useEffect(() => {
    if (!isAuthenticated && !clinicUser) {
      navigate('/login');
      return;
    }
    // PROVIDER-SALES-SURFACE-ALIGNMENT (Batch A) — fetch whenever the clinic user is
    // hydrated. The previous `!isSalesRep` gate (isSalesRep = primary role ===
    // 'provider_sales_rep') silently skipped the fetch for a dual-role account that
    // holds provider_sales_rep as a SECONDARY role (e.g. provider_admin + sales rep),
    // leaving My Leads at 0/"No Leads Found" even though leads are assigned. The route
    // guard already restricts who reaches this screen and the backend still scopes the
    // response per the caller's effective permissions — so this does not broaden access.
    if (!clinicUser) return;
    loadData();
  }, [clinicUser, loadData, navigate, isAuthenticated]);

  // P3-1 FIX v3: Use useCallback for filterLeads and include it in useEffect deps
  // Sprint 7 Batch 3: Added sorting support
  const filterLeads = React.useCallback(() => {
    let filtered = [...leads];

    // SALES-CRM-SIMPLIFICATION (Batch A) — status axis is the backend human stage only.
    // `ALL` shows every assigned lead (Total card == rendered rows); any other value is
    // one of NEW/WORKING/ON_HOLD/WON/LOST, matched via the same projection the rows render.
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(lead => lead.status_transitions?.human_stage === statusFilter);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(lead => {
        // RENEWAL-CYCLE-01 / PII: marketplace identities are masked pre-deposit
        // ("S***"), so a full-name query can never match them. Searching by the
        // reference the user CAN see is added instead of unmasking anything.
        const patientName = lead.patient_name || lead.patientName || '';
        return patientName.toLowerCase().includes(query) ||
          (lead.treatment_interest || lead.treatment || '').toLowerCase().includes(query) ||
          (lead.treatment_display_name || '').toLowerCase().includes(query) ||
          (lead.patient_email || '').toLowerCase().includes(query) ||
          String(lead.id || '').toLowerCase().includes(query) ||
          String(lead.country_of_origin || '').toLowerCase().includes(query);
      });
    }
    
    // Sprint 7 Batch 3: Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (leadSort.column) {
        case 'createdAt':
          // RENEWAL-CYCLE-01 — order by the CURRENT request cycle so a renewed
          // request is not buried under its original creation date. Leads that
          // were never renewed keep their exact previous position.
          comparison = new Date(a.current_cycle_started_at || a.created_at || a.createdAt || 0)
            - new Date(b.current_cycle_started_at || b.created_at || b.createdAt || 0);
          break;
        case 'patientName':
          comparison = (a.patient_name || a.patientName || '').localeCompare(b.patient_name || b.patientName || '');
          break;
        case 'treatment':
          comparison = (a.treatment_interest || a.treatment || '').localeCompare(b.treatment_interest || b.treatment || '');
          break;
        default:
          comparison = new Date(b.current_cycle_started_at || b.created_at || b.createdAt || 0)
            - new Date(a.current_cycle_started_at || a.created_at || a.createdAt || 0);
      }
      
      return leadSort.direction === 'asc' ? comparison : -comparison;
    });
    
    setFilteredLeads(filtered);
  }, [leads, statusFilter, searchQuery, leadSort]);

  useEffect(() => {
    filterLeads();
  }, [filterLeads]);
  
  // SPRINT 9 Batch 2.1: Clear selection when filters change
  useEffect(() => {
    setSelectedLeadIds(new Set());
  }, [statusFilter, searchQuery]);

  // SALES-CRM-SIMPLIFICATION (Batch A) — funnel counts derive from the SAME backend
  // human-stage projection the rows render, so every stat card count equals exactly the
  // population its click-through filter shows.
  const stageCount = (stage) => leads.filter(l => l.status_transitions?.human_stage === stage).length;
  const newCount = stageCount('NEW');
  const workingCount = stageCount('WORKING');
  const onHoldCount = stageCount('ON_HOLD');
  const wonCount = stageCount('WON');
  const lostCount = stageCount('LOST');

  // SPRINT 8 BATCH 3: Handler for adding audit notes to closed leads
  const handleAddAuditNote = async (leadId) => {
    if (!auditNoteText.trim()) return;
    try {
      const result = await apiAddNote(leadId, auditNoteText.trim());
      toast({
        title: result.is_audit ? 'Audit Note Added' : 'Note Added',
        description: result.is_audit ? 'Note recorded as audit entry on closed lead.' : 'Note added successfully.',
      });
      setAuditNoteText('');
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to add note', variant: 'destructive' });
    }
  };
  
  // SPRINT 9 Batch 2.1: Multi-select functions
  const toggleLeadSelection = (leadId) => {
    setSelectedLeadIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };
  
  const toggleSelectAllVisible = () => {
    const visibleIds = filteredLeads.map(l => l.id);
    const allSelected = visibleIds.every(id => selectedLeadIds.has(id));
    
    if (allSelected) {
      // Deselect all visible
      setSelectedLeadIds(prev => {
        const newSet = new Set(prev);
        visibleIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // Select all visible
      setSelectedLeadIds(prev => {
        const newSet = new Set(prev);
        visibleIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  };
  
  const clearSelection = () => {
    setSelectedLeadIds(new Set());
  };
  
  // SPRINT 9 Batch 2.1: Count immutable leads in selection
  const isLeadTerminal = (lead) => CLOSED_STATUSES.includes(lead.status) || ['LOST', 'COMPLETED'].includes(lead.status);
  
  const getSelectionStats = () => {
    const selectedLeads = filteredLeads.filter(l => selectedLeadIds.has(l.id));
    const immutableCount = selectedLeads.filter(l => isLeadTerminal(l)).length;
    const mutableCount = selectedLeads.length - immutableCount;
    return { total: selectedLeads.length, immutableCount, mutableCount };
  };
  
  const selectionStats = selectedLeadIds.size > 0 ? getSelectionStats() : null;
  
  // SPRINT 9 Batch 2.3: Get selected leads for bulk operations
  const getSelectedLeads = () => {
    return filteredLeads.filter(l => selectedLeadIds.has(l.id));
  };
  
  // SPRINT 9 Batch 2.3: Handle bulk status update via API
  const handleBulkStatusUpdate = async (leadId, newStatus) => {
    try {
      return await apiUpdateStatus(leadId, newStatus);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Status update failed', variant: 'destructive' });
      return null;
    }
  };
  
  // SPRINT 9 Batch 2.3: Handle bulk status complete
  const handleBulkStatusComplete = () => {
    loadData(); // Refresh from API
    clearSelection();
    toast({
      title: t('bulkStatus.success', 'Status Update Complete'),
      description: t('bulkStatus.successDesc', 'Selected leads have been updated.')
    });
  };

  // P3-DEBUG removed: data is now API-driven, no local state to reset

  // PROVIDER-LOGOUT-ISOLATION-01 — canonical logout (see
  // ClinicAdminDashboard). `logoutClinic()` alone left the HttpOnly auth
  // cookie live and every RBAC/workspace mirror in place.
  const handleLogout = () => {
    logoutClinic();
    clearRBAC();
    clearUnifiedAuth();
    if (logout) logout();
    navigate('/login');
  };

  const handleViewLead = (lead) => {
    setSelectedLead(lead);
    setShowLeadDetail(true);
  };

  // VIEW-LEAD-WORKSPACE-01 — "View lead" in Messages deep-links a
  // representative to their OWN workspace (`/clinic/sales?lead=<id>`) instead
  // of the admin-only lead page. The lead opens from the list this user is
  // already authorized to see, so no new permission or endpoint is involved.
  useEffect(() => {
    // Read the query through the router (HashRouter owns the hash and rewrites
    // it, so `window.location.hash` is not a reliable source here).
    const wanted = searchParams.get('lead');
    if (!wanted || !leads.length || showLeadDetail) return;
    const match = leads.find((l) => l.id === wanted);
    if (match) {
      setSelectedLead(match);
      setShowLeadDetail(true);
      return;
    }
    // Not on the loaded page — resolve the single lead through the SAME scoped
    // list endpoint (a rep only ever receives their own assigned leads).
    let cancelled = false;
    clinicLeadAPI.listLeads(clinicId, { lead_id: wanted, limit: 1 })
      .then((res) => {
        const one = (res.data?.leads || [])[0];
        if (cancelled) return;
        if (one) {
          setSelectedLead(one);
          setShowLeadDetail(true);
        } else {
          toast({ title: t('provider.messaging.toast.errorTitle', 'Error'),
                  description: t('provider.messaging.toast.noLeadForThread',
                                 'That lead is not in your assigned list.'),
                  variant: 'destructive' });
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast({ title: t('provider.messaging.toast.errorTitle', 'Error'),
                  description: t('provider.messaging.toast.noLeadForThread',
                                 'That lead is not in your assigned list.'),
                  variant: 'destructive' });
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, searchParams]);

  const handleCreateOffer = async (lead, offerToRevise = null) => {
    setSelectedLead(lead);
    setShowLeadDetail(false);
    // TREATX-BATCH-1.1 — prefetch existing offers BEFORE opening the
    // composer so revision-mode prefill / parent_offer_id are set at
    // mount. Prior behaviour opened the composer immediately and
    // resolved existingOffer async — the composer mounted with
    // existingOffer=null and never resynced, so revisions silently
    // fell back to first-offer defaults.
    // OWNER-OFFER-REVISION-01 — when the representative pressed Revise on a
    // specific version, THAT version is the revision parent. Re-reading the
    // list and taking `is_latest`/row 0 silently revised a different offer.
    if (offerToRevise) {
      setExistingOfferForCompose(offerToRevise);
      setShowOfferComposer(true);
      return;
    }
    let latest = null;
    try {
      const data = await apiListOffers(lead.id);
      const versions = data.offers || [];
      latest = versions.find(o => o.is_latest) || versions[0] || null;
    } catch {
      latest = null;
    }
    setExistingOfferForCompose(latest);
    setShowOfferComposer(true);
  };

  const handleOpenChat = async (lead) => {
    // FASTFIX-SALESREP-MESSAGE-ICON-OPENS-CANONICAL-THREAD-01 (2026-06-12):
    // Deep-link the canonical lead thread directly.  ClinicMessagesV2 then
    // resolves the thread via the existing context_type='lead' + context_id
    // relationship (the SSOT already set up by
    // CANONICAL-ASSIGNMENT-THREAD-SYNC-01).  No new endpoint, no new
    // collection — re-uses the existing threads-list payload, which
    // already projects context_type/context_id.
    if (lead?.id) {
      navigate(`/clinic/messages?lead=${encodeURIComponent(lead.id)}`);
    } else {
      navigate('/clinic/messages');
    }
  };

  // Message sending via API
  const handleSendMessage = async () => {
    if (newMessage.trim() && selectedLead) {
      try {
        await apiAddMessage(selectedLead.id, newMessage.trim(), 'OUTBOUND');
        setNewMessage('');
        // FIX P1-C: Refresh messages after send so they appear in the conversation panel
        try {
          const msgData = await apiListMessages(selectedLead.id);
          const msgs = (msgData.messages || msgData || []).map(m => ({
            id: m.id,
            text: m.content,
            sender: m.direction === 'OUTBOUND' ? 'rep' : 'patient',
            timestamp: m.created_at || m.timestamp,
          }));
          setSelectedLead(prev => ({ ...prev, messages: msgs }));
        } catch (_) {}
        toast({ title: 'Message Sent', description: 'Communication logged.' });
      } catch (err) {
        toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to log message', variant: 'destructive' });
      }
    }
  };

  // Offer creation via API (replaces local createOffer)
  // P0-A FIX: Transform camelCase OfferComposer data to snake_case for backend OfferCreateRequest
  const handleOfferSent = async (offerData) => {
    if (!selectedLead) return;
    try {
      // Transform camelCase frontend fields to snake_case backend fields.
      // FASTFIX-OFFER-CREATE-422-DIAGNOSTICS-01 (2026-06-12):
      // OfferComposer's `validUntil` stores the validity *in days* (free
      // text), NOT an ISO date string. The previous mapping ran
      // `new Date("14") - new Date()` → NaN → `valid_days: null`, which
      // dropped the validity contract silently. Map it as an integer.
      const validDays = parseInt(offerData.validUntil, 10);
      const backendPayload = {
        treatment_name: offerData.treatment || selectedLead?.treatment_interest || '',
        base_price: parseFloat(offerData.basePrice) || 0,
        currency: offerData.currency,  // PHASE 2B/B1 — backend policy validates; no client default
        // TREATX-TAX-5 — provider-declared treatment VAT (null = not declared).
        provider_tax: buildProviderTaxPayload(offerData),
        included_items: (offerData.includedItems || []).map(item => ({
          name: item.name || '',
          description: item.description || '',
          price: parseFloat(item.price) || 0,
          included: item.included !== false,
        })),
        notes_patient_visible: offerData.notes || '',
        notes_internal: '',
        valid_days: Number.isFinite(validDays) && validDays > 0 ? validDays : 30,
        hotel_nights: offerData.hotelNights ? parseInt(offerData.hotelNights, 10) : null,
        hotel_type: offerData.hotelType || null,
        airport_transfer: offerData.airportTransfer ?? null,
        proposed_dates: offerData.proposedDates || null,
        deposit_type: offerData.depositType || 'percentage',
        deposit_value: Number.isFinite(Number(offerData.depositValue)) && offerData.depositValue !== '' ? Number(offerData.depositValue) : null,
        payment_structure: offerData.paymentStructure || 'single',
        installment_count: offerData.paymentStructure === 'installments' ? Number(offerData.installmentCount) : null,
        additional_services: (offerData.additionalServices || []).map(s => ({
          name: s.name || '',
          price: parseFloat(s.price) || 0,
          quantity: parseInt(s.quantity, 10) || 1,
        })),
        terms: offerData.terms || '',
        // FASTFIX-OFFER-COMPOSER-FEATURE-PARITY-01 — additive payload
        // fields already accepted by offer_service.create_offer.
        template_id: offerData.templateId || null,
        campaign_id: offerData.campaignId || null,
        discount_percentage: offerData.discountPercentage !== '' && offerData.discountPercentage != null
          ? parseFloat(offerData.discountPercentage) || 0
          : null,
        // TREATX-BATCH-1.1 — Revision lineage forwarded so the backend
        // stamps canonical parent_offer_id + source_offer_id.
        parent_offer_id: offerData.parentOfferId || null,
        // MULTIPLE-ACTIONABLE-ALTERNATIVES — intent from the composer. revise (with a
        // target) replaces only that offer; alternative adds a co-valid option.
        offer_mode: offerData.offer_mode || 'alternative',
        supersedes_quote_id: offerData.supersedes_quote_id || null,
        // PROVIDER-OWNED-DRAFT-01 — prepare only; the backend keeps it DRAFT,
        // does not notify the patient and does not move the lead.
        draft: offerData.draft === true,
      };
      const created = await apiCreateOffer(selectedLead.id, backendPayload);
      const createdOffer = created?.offer || created?.data?.offer || null;
      loadData();
      if (offerData.draft === true) {
        // Stay in context: review the draft in the SAME canonical offer view
        // and download its PDF. Nothing was sent to the patient.
        setViewingOffer(createdOffer);
        setViewingOfferLead(selectedLead);
        // Re-read the lineage so the version history (v1, v2 …) is visible
        // immediately instead of only after closing and reopening the panel.
        try {
          const list = await apiListOffers(selectedLead.id);
          setOfferVersions(list?.offers || (createdOffer ? [createdOffer] : []));
        } catch {
          setOfferVersions(createdOffer ? [createdOffer] : []);
        }
        setShowOfferComposer(false);
        setExistingOfferForCompose(null);
        toast({ title: t('offer.draft.createdTitle'), description: t('offer.draft.createdDesc') });
        return;
      }
      setShowOfferComposer(false);
      setSelectedLead(null);
      setExistingOfferForCompose(null);
      toast({ title: t('toast.offerSent', 'Offer Sent'), description: t('toast.offerSentDesc', 'Offer created and persisted.') });
    } catch (err) {
      // FASTFIX-OFFER-CREATE-422-DIAGNOSTICS-01 — surface the REAL
      // backend error instead of swallowing it under "Failed to create
      // offer". FastAPI 422 returns `detail` as an array of pydantic
      // error objects; flatten it into a human-readable string so the
      // toast actually tells the user (and the owner) what went wrong.
      const data = err?.response?.data;
      let description = 'Failed to create offer';
      const _policyCode = (data?.detail && typeof data.detail === 'object' && !Array.isArray(data.detail)) ? data.detail.error : null;
      if (_policyCode && ['CURRENCY_NOT_ALLOWED', 'CURRENCY_POLICY_UNAVAILABLE', 'CURRENCY_REQUIRED'].includes(_policyCode)) {
        description = formatOfferError(err, t, description);
      } else if (typeof data?.detail === 'string') {
        description = data.detail;
      } else if (Array.isArray(data?.detail)) {
        description = data.detail
          .map(e => {
            const field = Array.isArray(e?.loc) ? e.loc.filter(p => p !== 'body').join('.') : '';
            const msg = e?.msg || 'invalid';
            return field ? `${field}: ${msg}` : msg;
          })
          .join(' · ');
      } else if (typeof data?.error === 'string') {
        description = data.error;
      } else if (typeof data?.message === 'string') {
        description = data.message;
      } else if (err?.message) {
        description = err.message;
      }
      toast({ title: 'Could not create offer', description, variant: 'destructive' });
    }
  };

  // 6B-17: Get handoff notification context for confirmation modals
  const { triggerHandoffNotification } = useHandoffNotification();

  // PROVIDER-OWNED-DRAFT-01 — provider-side PDF of the SAME canonical quote.
  // Fetched with the app's authenticated client (the route is clinic-scoped),
  // so it also works for a patient who has never joined TreatX.
  const handleDownloadOfferPdf = async (offer) => {
    if (!offer?.id) return;
    try {
      const res = await api.get(`/clinic/quotes/${offer.id}/pdf?language=${i18n.language}`,
                                { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `offer-${String(offer.id).slice(0, 8)}-v${offer.version_number || 1}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Error', description: t('offer.draft.downloadFailed'), variant: 'destructive' });
    }
  };

  // Promote a reviewed DRAFT into a real offer. The backend re-runs the
  // canonical currency policy, so this fails closed while residence is unknown.
  useEffect(() => {
    const leadId = viewingOffer?.lead_id || viewingOfferLead?.id;
    if (!viewingOffer || viewingOffer.status !== 'DRAFT' || !leadId) { setDraftPolicy(null); return; }
    let cancelled = false;
    api.get(`/clinic-leads/${leadId}/currency-policy`)
      .then(r => { if (!cancelled) setDraftPolicy(r.data?.currency_policy || r.data || null); })
      .catch(() => { if (!cancelled) setDraftPolicy(null); });
    return () => { cancelled = true; };
  }, [viewingOffer, viewingOfferLead]);

  // "Send offer to patient" performed on an offer saved earlier: the same
  // canonical endpoint, the same quote id, the same figures. The toast reports
  // what actually happened — including a notification the mail provider did
  // not accept, and the rare case where the offer is sent but a version marker
  // or the lead stage lagged behind.
  const [sendingOffer, setSendingOffer] = useState(false);
  const handleSendSavedOffer = async (offer) => {
    const leadId = offer?.lead_id || viewingOfferLead?.id;
    if (!offer?.id || !leadId || sendingOffer) return;
    setSendingOffer(true);
    try {
      const { data } = await api.post(`/clinic-leads/${leadId}/offers/${offer.id}/issue`);
      const sent = data?.offer || null;
      if (sent) {
        setViewingOffer(sent);
        try {
          const list = await apiListOffers(leadId);
          setOfferVersions(list?.offers || [sent]);
        } catch { setOfferVersions([sent]); }
      }
      loadData();
      const notified = data?.patient_notification?.accepted === true;
      const lagged = sent && (sent.version_effects_applied === false
                              || sent.lead_status_applied === false);
      toast({ title: t('offer.draft.issuedTitle'),
              description: lagged ? t('offer.draft.issuedPartialDesc')
                : notified ? t('offer.draft.issuedDesc')
                           : t('offer.draft.issuedNotNotifiedDesc') });
    } catch (err) {
      const data = err?.response?.data || {};
      // The API envelope exposes the code either as `detail.code` (raw
      // HTTPException) or flattened to `code` by the global handler.
      const d = (data.detail && typeof data.detail === 'object') ? data.detail : data;
      const codeMessages = {
        REVISION_TARGET_NOT_ELIGIBLE: 'offer.draft.revisionTargetNotEligible',
        OFFER_NOT_DRAFT: 'offer.draft.alreadyIssued',
        OFFER_SUPERSEDED: 'offer.draft.supersededBlocked',
        OFFER_VALIDITY_EXPIRED: 'offer.draft.validityExpired',
      };
      const key = codeMessages[d?.code];
      toast({ title: t('offer.currencyPolicy.unavailableTitle'),
              description: key ? t(key)
                : ((d && (d.message || d)) || t('offer.draft.issueBlocked')),
              variant: 'destructive' });
    } finally {
      setSendingOffer(false);
    }
  };

  // PATIENT-INVITE-EMAIL-01 — truthful states only: created / delivered /
  // not delivered / already pending. A token is never reported as an e-mail.
  const handleInvitePatient = async (lead, resend = false) => {
    if (!lead?.id) return;
    if (!(lead.patient_email || lead.patientEmail)) {
      toast({ title: t('offer.invite.button'), description: t('offer.invite.noEmail'), variant: 'destructive' });
      return;
    }
    try {
      const data = await apiSendInvite(lead.id, '', resend);
      const d = data?.email_delivery || data?.data?.email_delivery || {};
      const email = (data?.invitation || data?.data?.invitation || {}).patient_email
        || lead.patient_email || lead.patientEmail;
      // PATIENT-INVITE-EMAIL-02 — an e-mail service ACCEPTING the message is the
      // strongest fact this flow has; it is never reported as "the patient
      // received it", and a mock/unconfigured provider is reported as not sent.
      const accepted = d.accepted_by_email_service === true;
      // OWNER-CRM-INVITE-01 — somebody who already holds a TreatX account is
      // never sent a registration invitation; the backend reports it and the
      // rep is told plainly, with the existing linking flow left untouched.
      if (data?.already_registered || data?.data?.already_registered) {
        toast({ title: t('offer.invite.alreadyMemberTitle'),
                description: t('offer.invite.alreadyMemberDesc', { email }) });
        setInviteState({ leadId: lead.id, accepted: false,
                         reason: 'patient_already_registered', acceptUrl: '' });
        return { alreadyRegistered: true };
      }
      const descKey = d.reason === 'invitation_already_pending'
        ? 'offer.invite.alreadyPendingDesc'
        : (accepted ? 'offer.invite.deliveredDesc' : 'offer.invite.notDeliveredDesc');
      toast({ title: t('offer.invite.createdTitle'), description: t(descKey, { email }),
              variant: accepted || d.reason === 'invitation_already_pending' ? undefined : 'destructive' });
      const inv = data?.invitation || data?.data?.invitation || {};
      setInviteState({
        leadId: lead.id,
        accepted,
        reason: d.reason,
        acceptUrl: d.accept_url || inv.accept_url || '',
      });
      return { accepted, reason: d.reason };
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast({ title: 'Error', description: (d && (d.message || d)) || t('offer.invite.failed'), variant: 'destructive' });
    }
  };

  // 6B-15, 6B-17: Enhanced status change with confirmation and handoff notifications
  // SALES-CRM-SIMPLIFICATION — explicit business actions write through the same canonical status endpoint
  const handleSalesAction = async (lead, newStatus, reason) => {
    try {
      await apiUpdateStatus(lead.id, newStatus, reason || undefined);
      loadData();
      // Keep an open offer-review panel in sync, otherwise its stage buttons
      // keep offering transitions that are no longer allowed.
      if (viewingOfferLead?.id === lead.id) {
        try {
          const { data } = await api.get(`/clinic-leads/${lead.id}/status-transitions`);
          setViewingOfferLead((prev) => (prev && prev.id === lead.id
            ? { ...prev, status: data?.current || newStatus, status_transitions: data || prev.status_transitions }
            : prev));
        } catch (_) { /* panel simply keeps its previous projection */ }
      }
      triggerHandoffNotification(lead.status, newStatus, lead);
      toast({ title: t('toast.statusUpdated'),
              description: t(`offer.crm.stage.${newStatus}`, newStatus.replace('_', ' ')) });
    } catch (err) {
      toast({ title: 'Error',
              description: apiErrorMessage(err, 'Status update failed'),
              variant: 'destructive' });
    }
  };

  // SPRINT-3: Handler for creating provider-owned leads (auto-assign to self, now API-driven)
  const handleCreateOwnPatient = async (leadData) => {
    try {
      const created = await apiCreateLead({
        ...leadData,
        assigned_agent_id: clinicUser?.id,
      });
      setShowAddPatientModal(false);
      // Refresh leads from API
      apiFetchLeads();
      toast({ 
        title: t('toast.patientAdded'), 
        description: t('toast.patientAddedDesc', { name: leadData.patient_name || leadData.patientName })
      });
      // OWNER-CRM-INVITE-01 — the lead is persisted FIRST; an invitation
      // failure can therefore never lose it, and a retry never creates a
      // second lead. Membership stays optional.
      const newLead = created?.lead || created;
      if (leadData.invite_after_create && newLead?.id
          && (newLead.patient_email || leadData.patient_email)) {
        await handleInvitePatient({
          ...newLead,
          patient_email: newLead.patient_email || leadData.patient_email,
        });
        apiFetchLeads();
      }
      return created;
    } catch (err) {
      // The lead was NOT created — surface the real reason as text (a 422
      // `detail` is a list of objects and must never be handed to React) and
      // re-throw so the modal keeps the form open with the typed data.
      toast({ title: 'Error',
              description: apiErrorMessage(err, 'Failed to create lead'),
              variant: 'destructive' });
      throw err;
    }
  };

  // Sprint 7 Batch 3: Sort toggle handler for leads
  const toggleLeadSort = (column) => {
    setLeadSort(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sprint 7 Batch 3: Render sort indicator inline
  const renderSortIndicator = (column) => {
    if (leadSort.column !== column) {
      return <span className="text-gray-300 ml-1">↕</span>;
    }
    return (
      <span className="text-blue-600 ml-1">
        {leadSort.direction === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays > 0) return t('common.daysAgo', { count: diffDays });
    if (diffHrs > 0) return t('common.hoursAgo', { count: diffHrs });
    return t('common.justNow');
  };

  // RBAC-RESILIENT RENDERING: Never return null. Use RBAC auth as primary gate.
  if (!isAuthenticated && !clinicUser) {
    // Truly not logged in — redirect handled by useEffect
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="sales-dashboard-redirect">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">{t('provider.salesRep.redirectingToLogin')}</p>
        </div>
      </div>
    );
  }
  if (!clinicUser) {
    // Authenticated (RBAC/JWT) but clinicUser not yet hydrated from sessionStorage/backend.
    // Show loading state while ClinicDataContext hydrates from /api/auth/unified/me.
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="sales-dashboard-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">{t('provider.salesRep.loadingSalesDashboard')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster />
      
      {/* P3-1: Header focused on "Which patients need my action now?" */}
      <header className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white sticky top-12 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <User className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold" data-testid="sales-dashboard-title">{t('dashboard.title')}</h1>
                <p className="text-sm text-blue-100">{clinicUser.clinicName}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 min-w-0">
              {/* SPRINT-3: Add Own Patient Button */}
              <Button 
                onClick={() => setShowAddPatientModal(true)} 
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="add-own-patient-btn"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {t('dashboard.addOwnPatient')}
              </Button>
              <Button
                onClick={() => setShowBulkImport(true)}
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                data-testid="bulk-import-patients-btn"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                {t('provider.leads.bulkImport.openAction')}
              </Button>
              <div className="flex items-center gap-2">
                {clinicUser.avatar && (
                  <img src={clinicUser.avatar} alt="" className="w-8 h-8 rounded-full border-2 border-white/30" />
                )}
                <span className="text-sm">{clinicUser.name}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/20" data-testid="sales-logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                {t('common.logout')}
              </Button>
            </div>
          </div>
          {/* Quick navigation bar */}
          <div className="flex gap-2 mt-3 border-t border-white/20 pt-3">
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 text-xs" onClick={() => navigate('/clinic/messages')} data-testid="sales-nav-messages">
              <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> Conversations
            </Button>
            {/* P0-2 FIX: Removed separate "Quote Requests" nav — sales reps operate from ONE lead pipeline screen */}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* COA-10 G1 — Coordinator-assigned task inbox (post-acceptance) */}
        <SalesRepTaskInbox />

        {/* P3-1: Primary Action Banner — new leads awaiting first action (human stage NEW) */}
        {newCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="urgent-leads-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">
                    {t('dashboard.needsActionBanner', { count: newCount })}
                  </p>
                  <p className="text-sm text-amber-600">{t('dashboard.needsActionFilter')}</p>
                </div>
              </div>
              <Button 
                size="sm" 
                className="bg-amber-600 hover:bg-amber-700"
                onClick={() => setStatusFilter('NEW')}
                data-testid="view-urgent-leads-btn"
              >
                {t('dashboard.viewNow')}
              </Button>
            </div>
          </div>
        )}

        {/* Stats Cards — SALES-CRM-SIMPLIFICATION (Batch A): coherent human funnel.
            Total + New / Working / On Hold / Won / Lost. Each card's count and its
            click-through filter use the SAME human-stage predicate. */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Card className={`bg-gradient-to-br from-blue-500 to-blue-600 text-white cursor-pointer hover:shadow-lg ${statusFilter === 'ALL' ? 'ring-2 ring-blue-300' : ''}`} onClick={() => setStatusFilter('ALL')} data-testid="stat-total">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">{t('stats.total')}</p>
                  <p className="text-3xl font-bold">{stats.totalLeads || 0}</p>
                </div>
                <Users className="h-8 w-8 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className={`cursor-pointer hover:shadow-lg border-sky-200 ${statusFilter === 'NEW' ? 'ring-2 ring-sky-500' : ''}`} onClick={() => setStatusFilter('NEW')} data-testid="stat-new">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('provider.leads.stage.NEW')}</p>
                  <p className="text-3xl font-bold text-sky-600">{newCount}</p>
                </div>
                <Sparkles className="h-8 w-8 text-sky-500" />
              </div>
            </CardContent>
          </Card>

          <Card className={`cursor-pointer hover:shadow-lg border-indigo-200 ${statusFilter === 'WORKING' ? 'ring-2 ring-indigo-500' : ''}`} onClick={() => setStatusFilter('WORKING')} data-testid="stat-working">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('provider.leads.stage.WORKING')}</p>
                  <p className="text-3xl font-bold text-indigo-600">{workingCount}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-indigo-500" />
              </div>
            </CardContent>
          </Card>

          <Card className={`cursor-pointer hover:shadow-lg ${statusFilter === 'ON_HOLD' ? 'ring-2 ring-slate-500' : ''}`} onClick={() => setStatusFilter('ON_HOLD')} data-testid="stat-on-hold">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('provider.leads.stage.ON_HOLD')}</p>
                  <p className="text-3xl font-bold text-slate-600">{onHoldCount}</p>
                </div>
                <Clock className="h-8 w-8 text-slate-500" />
              </div>
            </CardContent>
          </Card>

          <Card className={`cursor-pointer hover:shadow-lg ${statusFilter === 'WON' ? 'ring-2 ring-green-500' : ''}`} onClick={() => setStatusFilter('WON')} data-testid="stat-won">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('provider.leads.stage.WON')}</p>
                  <p className="text-3xl font-bold text-green-600">{wonCount}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className={`cursor-pointer hover:shadow-lg ${statusFilter === 'LOST' ? 'ring-2 ring-rose-500' : ''}`} onClick={() => setStatusFilter('LOST')} data-testid="stat-lost">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('provider.leads.stage.LOST')}</p>
                  <p className="text-3xl font-bold text-rose-600">{lostCount}</p>
                </div>
                <Archive className="h-8 w-8 text-rose-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* SALES_DASHBOARD_PIPELINE_CONSISTENCY — Pipeline widget removed.
            It used a SECOND, legacy raw-status classifier (Needs Action / In Progress /
            Won / Lost) that diverged from the backend human_stage funnel the stat cards
            use (e.g. QUOTE_EXPIRED = human_stage WORKING was absent from the legacy
            In Progress list → 2 vs 3). Aligned to the same funnel it becomes pure
            duplication of the cards above, so per the product rule it is removed. The
            six stat cards (Total + New/Working/On Hold/Won/Lost) are the single funnel. */}

        {/* P3-FIX-1: Show active filter indicator */}
        {statusFilter !== 'ALL' && (
          <div className="flex items-center gap-2 mb-4 p-2 bg-blue-50 rounded-lg" data-testid="active-filter-indicator">
            <span className="text-sm text-blue-700">
              {t('dashboard.showingFilter', { filter: t(`provider.leads.stage.${statusFilter}`) })}
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setStatusFilter('ALL')}
              className="text-blue-600 hover:text-blue-800 h-6 px-2"
            >
              {t('dashboard.clearFilter')}
            </Button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('dashboard.searchLeads')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-leads-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">{t('dashboard.allStatus')}</option>
              {HUMAN_STAGES.map(stage => (
                <option key={stage} value={stage} data-testid={`stage-filter-option-${stage}`}>{t(`provider.leads.stage.${stage}`)}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Sprint 10: Trial Feature Notice */}
        {currentPackage?.id === 'TRIAL' && (
          <TrialFeatureNotice className="mb-4" />
        )}
        
        {/* SPRINT 9 Batch 2.1: Bulk Action Toolbar - Sprint 10: Feature Gated */}
        {selectedLeadIds.size > 0 && (
          <div className="sticky top-0 z-10 flex items-center gap-4 p-3 bg-blue-50 border border-blue-200 rounded-lg shadow-sm mb-4" data-testid="bulk-action-toolbar">
            <div className="flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-blue-600" />
              <span className="font-medium text-blue-900">
                {selectionStats?.total} {t('leadManagement.leadsSelected', 'leads selected')}
              </span>
              {selectionStats?.immutableCount > 0 && (
                <span className="text-sm text-amber-600 flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  {selectionStats.immutableCount} {t('leadManagement.locked', 'locked')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              {/* SPRINT 9 Batch 2.3: Bulk Status Button - Sprint 10: Gated */}
              <GatedButton 
                feature="bulkOperations"
                variant="outline" 
                size="sm" 
                onClick={() => setShowBulkStatusModal(true)}
                data-testid="bulk-status-btn"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                {t('leadManagement.bulkStatus', 'Change Status')}
              </GatedButton>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearSelection}
                className="text-gray-600 hover:text-gray-800"
                data-testid="clear-selection-btn"
              >
                <X className="h-4 w-4 mr-1" />
                {t('leadManagement.clearSelection', 'Clear')}
              </Button>
            </div>
          </div>
        )}

        {/* Leads List - SPRINT-4: Default to list view for scalability */}
        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('dashboard.myAssignedLeads')}</h2>
        
        {filteredLeads.length > 0 ? (
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            {/* List Header - 6B-16: Added Owner column */}
            {/* Sprint 7 Batch 3: Added sortable headers */}
            {/* Sprint 9 Batch 2.1: Added checkbox column - adjusted to fit 12-col grid */}
            <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wider">
              {/* SPRINT 9 Batch 2.1: Select All Checkbox */}
              <div className="col-span-1 flex items-center">
                <Checkbox
                  checked={filteredLeads.length > 0 && filteredLeads.every(l => selectedLeadIds.has(l.id))}
                  onCheckedChange={toggleSelectAllVisible}
                  aria-label={t('leadManagement.selectAll', 'Select all')}
                  data-testid="select-all-checkbox"
                />
              </div>
              <div 
                className="col-span-2 cursor-pointer hover:text-gray-700 select-none"
                onClick={() => toggleLeadSort('patientName')}
                data-testid="sort-patient"
              >
                {t('leads.patient')}
                {renderSortIndicator('patientName')}
              </div>
              <div 
                className="col-span-2 cursor-pointer hover:text-gray-700 select-none"
                onClick={() => toggleLeadSort('treatment')}
                data-testid="sort-treatment"
              >
                {t('leads.requestedTreatment')}
                {renderSortIndicator('treatment')}
              </div>
              <div className="col-span-2">{t('leads.status')}</div>
              <div className="col-span-1">{t('leads.assignedTo', 'Assigned To')}</div>
              <div 
                className="col-span-1 cursor-pointer hover:text-gray-700 select-none"
                onClick={() => toggleLeadSort('createdAt')}
                data-testid="sort-activity"
                title={t('leads.leadAgeHelp')}
              >
                <span data-testid="lead-age-header">{t('leads.leadAge')}</span>
                {renderSortIndicator('createdAt')}
              </div>
              <div className="col-span-3 text-right">{t('leads.actions')}</div>
            </div>
            
            {/* Lead Rows */}
            <div className="divide-y divide-gray-100">
              {filteredLeads.map(lead => {
                // OFFER-LIFECYCLE-PRODUCTION-TRUTH-CLOSURE — offer existence and
                // revise eligibility are projected server-side from the canonical
                // `quotes` authority (`offer_summary`), never re-derived from
                // lead.status vocabulary on the client.
                const offerSummary = lead.offer_summary || null;
                // PROVIDER-OWNED-DRAFT-03 — a prepared DRAFT is not an offer
                // (`count` excludes it, by design), but the representative must
                // still be able to re-open it for review. Both numbers come from
                // the same canonical projection.
                const hasOffer = offerSummary
                  ? (offerSummary.count > 0 || (offerSummary.draft_count || 0) > 0)
                  : ['OFFER_SENT', 'QUOTE_SENT', 'NEGOTIATION', 'QUOTE_EXPIRED', 'QUOTE_REVISED', 'WON', 'ORDER_CREATED'].includes(lead.status);
                const latestAccepted = offerSummary?.latest_status === 'ACCEPTED';
                // LIFECYCLE-V3.2 — commercial openness comes from backend authority, not a client status list
                const isTerminal = lead.status_transitions ? !lead.status_transitions.commercially_open : (latestAccepted || ['DEPOSIT_PAID', 'CLOSED_WON', 'CLOSED_LOST', 'CLOSED_CANCELLED', 'WON', 'ORDER_CREATED', 'PAID', 'COMPLETED'].includes(lead.status));
                const canMarkWon = !!lead.status_transitions?.can_mark_won;
                const isClosed = CLOSED_STATUSES.includes(lead.status);
                // SPRINT 8 BATCH 3: Check if lead is immutable
                const isImmutable = isLeadTerminal(lead);
                // SPRINT 8: Resolve patient from patientId
                const patient = getPatientForLead(lead);
                // SPRINT 9 Batch 2.1: Check if selected
                const isSelected = selectedLeadIds.has(lead.id);
                
                return (
                  <div 
                    key={lead.id} 
                    className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 py-3 hover:bg-gray-50 transition-colors items-center ${isSelected ? 'bg-blue-50/50' : ''} ${isClosed ? 'bg-gray-50/50' : ''}`}
                  >
                    {/* SPRINT 9 Batch 2.1: Row Checkbox */}
                    <div className="col-span-1 flex items-center gap-1">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleLeadSelection(lead.id)}
                        aria-label={`Select ${patient.name}`}
                        data-testid={`select-lead-${lead.id}`}
                      />
                      {isImmutable && (
                        <span title={t('leadManagement.lockedTooltip', 'This lead is closed and will be skipped in bulk operations')}>
                          <Lock className="h-3 w-3 text-gray-400" />
                        </span>
                      )}
                    </div>
                    {/* Patient Name & Country */}
                    <div className="col-span-2 flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0 ${
                        isClosed ? 'bg-gray-400' : 'bg-gradient-to-br from-blue-500 to-purple-500'
                      }`}>
                        {patient.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="min-w-0">
                        <p className={`font-medium truncate text-sm ${isClosed ? 'text-gray-500' : 'text-gray-900'}`}>{patient.name}</p>
                        <p className="text-xs text-gray-500 truncate">{patient.country}</p>
                      </div>
                    </div>
                    
                    {/* Treatment */}
                    <div className="col-span-2">
                      <p className={`text-sm truncate ${isClosed ? 'text-gray-500' : 'text-gray-900'}`} title={lead.treatment_display_name || lead.treatment_interest} data-testid={`lead-treatment-${lead.id}`}>{lead.treatment_display_name || lead.treatment_interest || lead.treatment}</p>
                    </div>
                    
                    {/* Status — SALES-CRM-SIMPLIFICATION: human stage + read-only context + explicit actions (no stage dropdown) */}
                    <div className="col-span-2 flex flex-col gap-1" data-testid={`lead-stage-cell-${lead.id}`}>
                      {/* RENEWAL-CYCLE-01 — the patient's CURRENT request cycle is
                          primary work; the historical offer stage stays below it. */}
                      {(lead.request_cycle || 1) > 1 && (
                        <span
                          className="inline-flex w-fit items-center rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-800"
                          title={t('provider.leads.renewal.tooltip', {
                            cycle: lead.request_cycle,
                            date: formatDateLocale(lead.renewed_at || lead.current_cycle_started_at),
                          })}
                          data-testid={`lead-renewal-${lead.id}`}
                        >
                          {t('provider.leads.renewal.badge', { cycle: lead.request_cycle })}
                        </span>
                      )}
                      <SalesStageBadge transitions={lead.status_transitions} fallbackLabel={statusLabelI18n(t, lead.status)} />
                      <SalesActions
                        lead={lead}
                        transitions={lead.status_transitions}
                        compact
                        idSuffix={lead.id}
                        onStatus={(status, reason) => handleSalesAction(lead, status, reason)}
                        onMarkWon={() => setMarkWonLead(lead)}
                        onDone={() => loadData && loadData()}
                      />
                    </div>
                    
                    {/* P1-7 FIX: Show assignee. Assignment-method provenance ("Manual" etc.)
                        removed from the row — non-actionable metadata (audit field retained in DB). */}
                    <div className="col-span-1 hidden md:block">
                      <span className="text-xs font-medium text-gray-700 truncate">
                        {lead.assigned_agent_id === 'SYSTEM_POOL' ? 'System Pool' : (lead.assigned_agent_name || lead.assignedAgentName || 'Unassigned')}
                      </span>
                    </div>
                    
                    {/* Last Activity / Closed Date - removed Package column */}
                    {/* Last Activity / Closed Date */}
                    <div className="col-span-1 hidden md:flex items-center gap-1 text-xs text-gray-500">
                      {isClosed && lead.closedAt ? (
                        <>
                          <Archive className="h-3 w-3" />
                          {formatDateLocale(lead.closedAt)}
                        </>
                      ) : (
                        <>
                          <Clock className="h-3 w-3" />
                          <span title={t('leads.leadAgeHelp')} data-testid={`lead-age-${lead.id}`}>{getTimeAgo(lead.current_cycle_started_at || lead.created_at || lead.createdAt)}</span>
                        </>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="col-span-3 flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewLead(lead)}
                        className="h-8 px-2 text-gray-600 hover:text-blue-600"
                      >
                        <Eye className="h-4 w-4" />
                        <span className="hidden sm:inline ml-1">{t('common.details')}</span>
                      </Button>
                      
                      {/* SPRINT 8 BATCH 3: Disable chat for immutable leads */}
                      {!isImmutable ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenChat(lead)}
                          className="h-8 px-2 text-gray-600 hover:text-blue-600"
                          data-testid={`sales-lead-message-icon-${lead.id}`}
                        >
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                      ) : (
                        <span 
                          className="h-8 px-2 flex items-center text-gray-400 cursor-not-allowed"
                          title={t('provider.salesRep.messagesDisabledClosed')}
                        >
                          <MessageCircle className="h-4 w-4 opacity-50" />
                        </span>
                      )}
                      
                      {/* P3-REVISION: Offer buttons based on lead status */}
                      {/* SPRINT 8 BATCH 3: Show locked indicator for immutable leads */}
                      {isImmutable ? (
                        <span className="h-8 px-3 flex items-center gap-1 text-gray-400 text-xs">
                          <Lock className="h-3 w-3" />
                          <span className="hidden sm:inline">Locked</span>
                        </span>
                      ) : hasOffer ? (
                        <>
                          {/* RC-8 FIX: View History always visible for leads with offers */}
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`view-offer-btn-${lead.id}`}
                            onClick={async () => {
                              try {
                                const data = await apiListOffers(lead.id);
                                const versions = data.offers || [];
                                setOfferVersions(versions);
                                setViewingOfferLead(lead);
                                const latest = versions.find(o => o.is_latest) || versions[0];
                                setViewingOffer(latest || null);
                              } catch { setViewingOffer(null); setOfferVersions([]); }
                            }}
                            className="h-8 px-2 text-purple-600 hover:text-purple-700"
                          >
                            <FileText className="h-4 w-4" />
                            <span className="hidden sm:inline ml-1">{t('offer.view')}</span>
                          </Button>
                          {!isTerminal && (
                            <Button
                              size="sm"
                              data-testid={`revise-offer-btn-${lead.id}`}
                              onClick={() => handleCreateOffer(lead)}
                              className="h-8 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                            >
                              <Send className="h-4 w-4" />
                              <span className="hidden sm:inline ml-1">{t('offer.revise')}</span>
                            </Button>
                          )}
                        </>
                      ) : !isTerminal ? (
                        <Button
                          size="sm"
                          data-testid={`create-offer-btn-${lead.id}`}
                          onClick={() => handleCreateOffer(lead)}
                          className="h-8 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                        >
                          <Send className="h-4 w-4" />
                          <span className="hidden sm:inline ml-1">{t('offer.offer')}</span>
                        </Button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* List Footer - Lead count */}
            <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-500">
              {t('dashboard.showingLeads', { count: filteredLeads.length })}
            </div>
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('dashboard.noLeadsTitle')}</h3>
              <p className="text-gray-500">
                {statusFilter !== 'ALL' || searchQuery 
                  ? t('dashboard.noLeadsFiltered')
                  : t('dashboard.noLeadsMessage')}
              </p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Lead Detail Modal */}
      {showLeadDetail && selectedLead && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl my-8">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Lead Details</CardTitle>
                <CardDescription>{selectedLead.patient_name || selectedLead.patientName}</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowLeadDetail(false)}>
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* M16B-07B — Treatment Ready (per slug, clinic-evidence) */}
              <TreatmentReadyPanel
                leadId={selectedLead.id}
                mode="clinic"
                title={t('provider.salesRep.treatmentReadiness')}
              />

              {/* Contact Info */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">{t('provider.repDashboard.contactInfo')}</h4>
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{selectedLead.patient_email || selectedLead.patientEmail || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{selectedLead.patient_phone || selectedLead.patientPhone || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{selectedLead.country_of_origin || selectedLead.patientCountry || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Treatment Info */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">{t('provider.salesRep.treatmentDetails')}</h4>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <Badge className="mb-2">{selectedLead.treatment_interest || selectedLead.treatment}</Badge>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <p className="text-sm text-gray-500">{t('provider.salesRep.budget')}</p>
                      <p className="font-medium">{selectedLead.budget_range || selectedLead.intakeSummary?.budget || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('provider.salesRep.preferredDates')}</p>
                      <p className="font-medium">{selectedLead.intakeSummary?.preferredDates || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── SSO-D (audit defect D7) — the patient's own selection.
                  `campaign_attribution`, `preferred_doctor` and
                  `selection_events` were already on the lead payload but the
                  rep workspace never showed them. Read-only: this is the
                  ORIGINAL request, not the provider's proposal. */}
              <div data-testid="rep-lead-patient-selection">
                <h4 className="font-semibold text-gray-900 mb-3">
                  {t('provider.salesRep.patientSelection')}
                </h4>
                <div className="bg-slate-50 border border-slate-200 p-4 rounded-lg space-y-1 text-sm">
                  {selectedLead.campaign_attribution ? (
                    <>
                      <p data-testid="rep-lead-selected-offer">
                        <span className="text-gray-500">{t('provider.salesRep.selectedOffer')}: </span>
                        <span className="font-medium">
                          {selectedLead.campaign_attribution.campaign_title
                            || selectedLead.campaign_attribution.campaign_id}
                        </span>
                      </p>
                      <p className="text-xs text-gray-500" data-testid="rep-lead-selected-offer-provenance">
                        {selectedLead.campaign_attribution.patient_selected === true
                          ? t('provider.salesRep.offerChosenByPatient')
                          : selectedLead.campaign_attribution.patient_selected === false
                            ? t('provider.salesRep.offerAttachedAutomatically')
                            : t('provider.salesRep.offerProvenanceUnknown')}
                      </p>
                    </>
                  ) : (
                    <p data-testid="rep-lead-standard-treatment">
                      <span className="text-gray-500">{t('provider.salesRep.selectedOffer')}: </span>
                      <span className="font-medium">{t('provider.salesRep.standardTreatmentRequested')}</span>
                    </p>
                  )}
                  {selectedLead.preferred_doctor?.doctor_id && (
                    <p data-testid="rep-lead-preferred-doctor">
                      <span className="text-gray-500">{t('provider.salesRep.preferredDoctor')}: </span>
                      <span className="font-medium">
                        {selectedLead.preferred_doctor.full_name || selectedLead.preferred_doctor.doctor_id}
                      </span>
                      <span className="ml-1 text-xs text-gray-500">
                        {t('provider.salesRep.preferenceNotAssignment')}
                      </span>
                    </p>
                  )}
                  {/* SSO-CLOSURE-C — the frozen attribution above is the
                      ORIGINAL request. When the patient has since changed what
                      they want, the LATEST event is what the rep must quote
                      against, so it is shown explicitly rather than hidden
                      behind a counter. Neither is an accepted agreement. */}
                  {Array.isArray(selectedLead.selection_events)
                    && selectedLead.selection_events.length > 1 && (() => {
                      const events = selectedLead.selection_events;
                      const latest = events[events.length - 1];
                      const frozenId = selectedLead.campaign_attribution?.campaign_id || null;
                      const latestId = latest?.campaign_id || null;
                      return (
                        <div className="mt-2 pt-2 border-t border-slate-200" data-testid="rep-lead-latest-selection">
                          <p className="text-xs font-semibold text-gray-700">
                            {t('provider.salesRep.latestSelection')}
                          </p>
                          <p data-testid="rep-lead-latest-selection-offer">
                            <span className="text-gray-500">{t('provider.salesRep.selectedOffer')}: </span>
                            <span className="font-medium">
                              {latestId || t('provider.salesRep.standardTreatmentRequested')}
                            </span>
                          </p>
                          {latestId !== frozenId && (
                            <p className="text-xs text-amber-700" data-testid="rep-lead-selection-changed">
                              {t('provider.salesRep.selectionChangedSinceOriginal')}
                            </p>
                          )}
                          {latest?.preferred_doctor_id
                            && latest.preferred_doctor_id !== selectedLead.preferred_doctor?.doctor_id && (
                            <p className="text-xs text-gray-500" data-testid="rep-lead-latest-doctor">
                              {t('provider.salesRep.preferredDoctor')}: {latest.preferred_doctor_id}
                            </p>
                          )}
                          <p className="text-xs text-gray-500" data-testid="rep-lead-selection-updates">
                            {t('provider.salesRep.selectionUpdated', { count: events.length - 1 })}
                            {latest?.at ? ` · ${String(latest.at).slice(0, 16).replace('T', ' ')}` : ''}
                          </p>
                        </div>
                      );
                    })()}
                </div>
              </div>

              {/* Assignment Panel — Ownership + History */}
              <AssignmentPanel
                entityType="lead"
                entityId={selectedLead.id}
                clinicId={clinicId}
                userRole={userRole}
                onAssignmentChange={() => apiFetchLeads()}
              />

              {/* 6B-03: Closed Lead Info - Show closedAt and closedReason for closed leads */}
              {CLOSED_STATUSES.includes(selectedLead.status) && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Closure Details</h4>
                  <div className={`p-4 rounded-lg ${
                    selectedLead.status === 'CLOSED_WON' ? 'bg-green-50' :
                    selectedLead.status === 'CLOSED_LOST' ? 'bg-red-50' : 'bg-gray-50'
                  }`}>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">{t('provider.salesRep.status')}</p>
                        {/* RENEWAL-CYCLE-01 — current request cycle first, historical
                            offer stage second: the two must never contradict. */}
                        {(selectedLead.request_cycle || 1) > 1 && (
                          <div className="mb-1" data-testid={`lead-detail-renewal-${selectedLead.id}`}>
                            <span className="inline-flex items-center rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-800">
                              {t('provider.leads.renewal.badge', { cycle: selectedLead.request_cycle })}
                            </span>
                            <p className="mt-1 text-xs text-gray-600">
                              {t('provider.leads.renewal.requestedOn', {
                                date: formatDateLocale(selectedLead.renewed_at || selectedLead.current_cycle_started_at),
                              })}
                            </p>
                            {(() => {
                              const evs = (selectedLead.selection_events || []).filter(
                                (e) => (e.request_cycle || 1) === (selectedLead.request_cycle || 1)
                              );
                              const note = evs.length ? evs[evs.length - 1] : null;
                              return note ? (
                                <p className="mt-1 text-xs text-gray-700" data-testid={`lead-detail-latest-request-${selectedLead.id}`}>
                                  {t('provider.leads.renewal.latestRequest', {
                                    treatment: note.treatment_interest || selectedLead.treatment_display_name || selectedLead.treatment_interest || '',
                                    date: formatDateLocale(note.at),
                                  })}
                                </p>
                              ) : null;
                            })()}
                          </div>
                        )}
                        <SalesStageBadge transitions={selectedLead.status_transitions} fallbackLabel={statusLabelI18n(t, selectedLead.status)} />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('provider.salesRep.closedOn')}</p>
                        <p className="font-medium">
                          {selectedLead.closedAt 
                            ? formatDateLocale(selectedLead.closedAt) 
                            : 'N/A'}
                        </p>
                      </div>
                      {selectedLead.closedReason && (
                        <div className="col-span-2">
                          <p className="text-sm text-gray-500">{t('provider.salesRep.reason')}</p>
                          <p className="font-medium">{selectedLead.closedReason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Health Info */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">{t('provider.salesRep.healthInformation')}</h4>
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-500">{t('provider.salesRep.age')}</p>
                    <p className="font-medium">{selectedLead.intakeSummary?.age || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('provider.salesRep.gender')}</p>
                    <p className="font-medium capitalize">{selectedLead.intakeSummary?.gender || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('provider.salesRep.healthConditions')}</p>
                    <p className="font-medium">{selectedLead.intakeSummary?.healthConditions || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('provider.salesRep.allergies')}</p>
                    <p className="font-medium">{selectedLead.intakeSummary?.allergies || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Notes Section - SPRINT 8 BATCH 3: Enhanced with audit note support */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  {t('provider.salesRep.notes')}
                  {isLeadTerminal(selectedLead) && (
                    <span className="text-xs font-normal text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                      {t('provider.salesRep.auditTrail')}
                    </span>
                  )}
                </h4>
                
                {/* Existing Notes */}
                {selectedLead.notes?.length > 0 ? (
                  <ul className="space-y-2 mb-4">
                    {selectedLead.notes.map((note, idx) => {
                      // Handle both string notes and audit note objects
                      const isAuditNote = typeof note === 'object' && note.type === 'AUDIT';
                      const noteText = typeof note === 'object' ? note.text : note;
                      
                      return (
                        <li key={idx} className={`p-2 rounded text-sm ${isAuditNote ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                          <div className="flex items-start gap-2">
                            {isAuditNote && (
                              <span className="text-amber-600 text-xs font-medium shrink-0">{t('provider.salesRep.audit')}</span>
                            )}
                            <span className="text-gray-700">{noteText}</span>
                          </div>
                          {isAuditNote && note.addedAt && (
                            <p className="text-xs text-amber-500 mt-1">
                              {t('provider.salesRep.auditAddedNote', { date: formatDateLocale(note.addedAt) })}
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 mb-4">{t('provider.salesRep.noNotesYet')}</p>
                )}
                
                {/* SPRINT 8 BATCH 3: Audit Note Input for Closed Leads */}
                {isLeadTerminal(selectedLead) && (
                  <div className="border-t pt-3">
                    <p className="text-xs text-amber-600 mb-2 flex items-center gap-1">
                      <Lock className="h-3 w-3" />
                      {t('provider.salesRep.leadClosedNote')}
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={auditNoteText}
                        onChange={(e) => setAuditNoteText(e.target.value)}
                        placeholder={t('provider.salesRep.addAuditNotePh')}
                        className="flex-1 px-3 py-2 text-sm border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 bg-amber-50"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && auditNoteText.trim()) {
                            handleAddAuditNote(selectedLead.id);
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        onClick={() => handleAddAuditNote(selectedLead.id)}
                        disabled={!auditNoteText.trim()}
                        className="bg-amber-500 hover:bg-amber-600 text-white"
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="pt-4 border-t flex gap-3">
                <Button variant="outline" onClick={() => setShowLeadDetail(false)} className="flex-none">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back to List
                </Button>
                {/* SPRINT 8 BATCH 3: Disable chat button for immutable leads */}
                {!isLeadTerminal(selectedLead) ? (
                  <Button variant="outline" onClick={() => handleOpenChat(selectedLead)} className="flex-1">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Open Chat
                  </Button>
                ) : (
                  <Button variant="outline" disabled className="flex-1 opacity-50 cursor-not-allowed">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Chat Disabled
                  </Button>
                )}
                {!['OFFER_SENT', 'DEPOSIT_PAID', 'CLOSED_WON', 'CLOSED_LOST', 'CLOSED_CANCELLED'].includes(selectedLead.status) && (
                  <Button onClick={() => handleCreateOffer(selectedLead)} className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600">
                    <Send className="h-4 w-4 mr-2" />
                    Create & Send Offer
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Chat Panel */}
      {showChatPanel && selectedLead && (
        <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col">
          <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
            <div>
              <h3 className="font-semibold">{selectedLead.patient_name || selectedLead.patientName}</h3>
              <p className="text-sm text-blue-100">{selectedLead.treatment_interest || selectedLead.treatment}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowChatPanel(false)} className="text-white">
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
            {selectedLead.messages?.length > 0 ? (
              <div className="space-y-3">
                {selectedLead.messages.map(msg => (
                  <div 
                    key={msg.id}
                    className={`flex ${msg.sender === 'rep' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] p-3 rounded-lg ${
                      msg.sender === 'rep' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-white border'
                    }`}>
                      <p className="text-sm">{msg.text}</p>
                      <p className={`text-xs mt-1 ${msg.sender === 'rep' ? 'text-blue-200' : 'text-gray-400'}`}>
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <MessageCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>{t('provider.salesRep.noMessagesYet')}</p>
                <p className="text-sm">{t('provider.salesRep.startConversation')}</p>
              </div>
            )}
          </div>

          <div className="p-4 border-t bg-white">
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={t('provider.salesRep.typeMessagePh')}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <Button onClick={handleSendMessage}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* P3-REVISION: View Sent Offer Modal */}
      {viewingOffer && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="w-full max-w-2xl my-8 bg-white rounded-xl shadow-2xl">
            <div className="p-6 border-b flex items-center justify-between bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-t-xl">
              <div>
                <h2 className="text-xl font-bold" data-testid="offer-modal-title">
                  {viewingOffer.status === 'DRAFT'
                    ? `${t('offer.draft.reviewTitle')} — v${viewingOffer.version_number || 1}`
                    : `Sent Offer - Version ${viewingOffer.version_number || viewingOffer.version || 1}`}
                </h2>
                {viewingOffer.status !== 'DRAFT' && (
                  <p className="text-purple-100">Sent on {formatDateLocale(viewingOffer.sent_at || viewingOffer.created_at || viewingOffer.createdAt)}</p>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    viewingOffer.status === 'ACCEPTED' ? 'bg-green-200 text-green-800' :
                    viewingOffer.status === 'VIEWED' ? 'bg-blue-200 text-blue-800' :
                    viewingOffer.status === 'REJECTED' ? 'bg-red-200 text-red-800' :
                    viewingOffer.status === 'EXPIRED' ? 'bg-gray-200 text-gray-800' :
                    viewingOffer.status === 'DRAFT' ? 'bg-amber-200 text-amber-900' :
                    'bg-yellow-200 text-yellow-800'
                  }`} data-testid="offer-status-badge">
                    {viewingOffer.status === 'DRAFT' ? t('offer.draft.badge')
                      : (viewingOffer.status || 'SENT')}
                  </span>
                  {offerVersions.length > 1 && (
                    <span className="text-purple-200 text-xs">{offerVersions.length} versions total</span>
                  )}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setViewingOffer(null); setOfferVersions([]); }} className="text-white hover:bg-white/20">
                <X className="h-5 w-5" />
              </Button>
            </div>
            {/* Version selector */}
            {offerVersions.length > 1 && (
              <div className="px-6 pt-3 pb-1 border-b bg-gray-50 flex items-center gap-2 flex-wrap" data-testid="offer-version-selector">
                <span className="text-xs font-medium text-gray-500 mr-1">{t('provider.salesRep.versions')}</span>
                {offerVersions.map((v) => (
                  <button
                    key={v.id}
                    data-testid={`offer-version-btn-${v.version_number || 1}`}
                    onClick={() => setViewingOffer(v)}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                      viewingOffer.id === v.id
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                    }`}
                  >
                    v{v.version_number || 1}{v.is_latest ? ' (latest)' : ''} — {v.status || 'SENT'}
                  </button>
                ))}
              </div>
            )}
            <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4">
              {/* PROVIDER-OWNED-DRAFT-01 — a draft can never read as issued. */}
              {viewingOffer.status === 'DRAFT' && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="offer-draft-notice">
                  <p className="font-semibold">{t('offer.draft.badge')}</p>
                  <p>{t('offer.draft.notBinding')}</p>
                  {viewingOffer.currency_verified === false && !draftPolicy?.resolved && (
                    <p className="mt-1" data-testid="offer-draft-currency-unverified">{t('offer.draft.currencyUnverified')}</p>
                  )}
                  {/* PROVIDER-OWNED-DRAFT-02 — residence now recorded: say
                      plainly whether this draft's currency can be issued. */}
                  {viewingOffer.currency_verified === false && draftPolicy?.resolved
                    && !(draftPolicy.allowed_currencies || []).includes(viewingOffer.currency) && (
                    <p className="mt-1" data-testid="offer-draft-currency-not-allowed">
                      {t('offer.currencyPolicy.currencyNotAllowed',
                         { allowed: (draftPolicy.allowed_currencies || []).join(', ') })}
                    </p>
                  )}
                </div>
              )}
              {/* The three facts a representative must not confuse: the offer is
                  sent, the e-mail service accepted it, the patient has an
                  account. A sent offer only becomes openable once the patient
                  accepts the invitation and signs in. */}
              {viewingOffer.status !== 'DRAFT'
                && !(viewingOfferLead?.patient_id || viewingOfferLead?.patientId) && (
                <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900"
                     data-testid="offer-no-patient-account-notice">
                  <p>{t('offer.invite.noAccountYet')}</p>
                </div>
              )}
              {/* OFFER-LIFECYCLE-PRODUCTION-TRUTH-CLOSURE — single shared commercial-facts projection */}
              <OfferCommercialFacts
                offer={viewingOffer}
                requestedTreatment={viewingOfferLead?.treatment_display_name || viewingOfferLead?.treatment_interest}
                language={i18n.language}
              />

              {/* Status & Valid Until */}
              <div className="flex items-center justify-between pt-4 border-t gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Badge className={viewingOffer.status === 'SENT' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}>
                    {(viewingOffer.superseded_at || viewingOffer.superseded_by_quote_id)
                      ? t('offer.superseded', 'Replaced by a newer version')
                      : viewingOffer.status === 'DRAFT'
                        ? t('offer.draft.badge')
                        : viewingOffer.status}
                  </Badge>
                  {(viewingOffer.valid_until || viewingOffer.validUntil) && (
                    <span className="text-sm text-gray-500">
                      Valid until {formatDateLocale(viewingOffer.valid_until || viewingOffer.validUntil)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Provider-side PDF of the SAME canonical quote — works for a
                      patient who has no TreatX account. */}
                  <Button variant="outline" onClick={() => handleDownloadOfferPdf(viewingOffer)}
                          data-testid="offer-download-pdf-btn">
                    <Download className="h-4 w-4 mr-1" />{t('offer.draft.downloadPdf')}
                  </Button>
                  {/* "Send offer to patient" for an offer that was saved
                      earlier — the SAME action and the same quote, gated by the
                      same canonical currency policy as a direct send. */}
                  {/* A preparation that has already been replaced by a newer
                      version can no longer be sent or revised — the canonical
                      door refuses it, so the action is not offered either. */}
                  {viewingOffer.status === 'DRAFT'
                   && (viewingOffer.superseded_at || viewingOffer.superseded_by_quote_id) && (
                    <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-800"
                          data-testid="offer-superseded-note">
                      {t('offer.superseded', 'Replaced by a newer version')}
                    </span>
                  )}
                  {viewingOffer.status === 'DRAFT'
                   && !viewingOffer.superseded_at && !viewingOffer.superseded_by_quote_id && (() => {
                    const verified = viewingOffer.currency_verified !== false;
                    const allowed = draftPolicy?.resolved
                      ? (draftPolicy.allowed_currencies || []) : null;
                    const policyAllows = allowed ? allowed.includes(viewingOffer.currency) : false;
                    const sendable = verified || policyAllows;
                    const blockedReason = allowed && !policyAllows
                      ? t('offer.currencyPolicy.currencyNotAllowed', { allowed: allowed.join(', ') })
                      : t(currencyPolicyReasonKey(draftPolicy?.reason));
                    return (
                      <Button onClick={() => handleSendSavedOffer(viewingOffer)}
                              disabled={!sendable || sendingOffer}
                              title={sendable ? undefined : blockedReason}
                              data-testid="offer-issue-btn"
                              className="bg-purple-600 hover:bg-purple-700 text-white">
                        {sendingOffer ? t('common.loading') : t('offer.draft.issueButton')}
                      </Button>
                    );
                  })()}
                  {!(viewingOfferLead?.patient_id || viewingOfferLead?.patientId) && (
                    <Button variant="outline" onClick={() => handleInvitePatient(viewingOfferLead)}
                            data-testid="offer-invite-patient-btn">
                      {t('offer.invite.button')}
                    </Button>
                  )}
                  {/* PATIENT-INVITE-EMAIL-03 — when the e-mail service did not
                      accept the message, the invitation still exists and its
                      link works. Offer the two honest remedies: retry THAT
                      invitation, or copy its link and send it yourself. */}
                  {inviteState && inviteState.leadId === viewingOfferLead?.id
                    && !inviteState.accepted && (
                    <>
                      <Button variant="outline" onClick={() => handleInvitePatient(viewingOfferLead, true)}
                              data-testid="offer-invite-retry-btn">
                        {t('offer.invite.retryButton')}
                      </Button>
                      {inviteState.acceptUrl && (
                        <Button variant="outline" data-testid="offer-invite-copy-link-btn"
                                onClick={() => {
                                  navigator.clipboard?.writeText(inviteState.acceptUrl);
                                  toast({ title: t('offer.invite.linkCopiedTitle'),
                                          description: t('offer.invite.linkCopiedDesc') });
                                }}>
                          {t('offer.invite.copyLinkButton')}
                        </Button>
                      )}
                    </>
                  )}
                  {/* OWNER-CRM-WORKFLOW-01 — the rep keeps working the funnel
                      from inside the offer review, with or without TreatX
                      membership and after a PDF download. These write through
                      the SAME canonical status endpoint and the SAME revision
                      composer used on the lead row — no second status
                      authority, and a CRM note never claims an online
                      acceptance or a received payment. */}
                  {viewingOfferLead && (
                    <>
                      <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700"
                            data-testid="offer-membership-state">
                        {(viewingOfferLead.patient_id || viewingOfferLead.patientId)
                          ? t('offer.invite.stateJoined')
                          : ((inviteState && inviteState.leadId === viewingOfferLead.id
                              && inviteState.reason !== 'patient_already_registered')
                             || viewingOfferLead.status === 'INVITED_TO_PLATFORM')
                            ? t('offer.invite.statePending')
                            : t('offer.invite.stateNoAccount')}
                      </span>
                      {viewingOfferLead.status_transitions?.commercially_open !== false && (
                        <>
                          {/* Stage buttons are driven by the canonical
                              `allowed_next` projection, so a rep is never
                              offered a transition the guard will refuse. */}
                          {(viewingOfferLead.status_transitions?.allowed_next || [])
                            .filter((s) => ['CONTACTED', 'QUALIFIED', 'NEGOTIATION', 'ON_HOLD'].includes(s))
                            .map((s) => (
                              <Button key={s} variant="outline"
                                      data-testid={`offer-crm-stage-${s.toLowerCase()}`}
                                      onClick={() => handleSalesAction(viewingOfferLead, s,
                                                                      'rep CRM update from offer review')}>
                                {t(`offer.crm.stage.${s}`, s.replace('_', ' '))}
                              </Button>
                            ))}
                          {!viewingOffer.superseded_at && !viewingOffer.superseded_by_quote_id && (
                            <Button variant="outline" data-testid="offer-crm-revise-btn"
                                    onClick={() => { const _o = viewingOffer; setViewingOffer(null); handleCreateOffer(viewingOfferLead, _o); }}>
                              {t('offer.revise')}
                            </Button>
                          )}
                        </>
                      )}
                    </>
                  )}
                  <Button onClick={() => setViewingOffer(null)} variant="ghost">
                    {t('offer.draft.close')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Offer Composer Modal */}
      {showOfferComposer && selectedLead && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="w-full max-w-3xl my-8 bg-white rounded-xl shadow-2xl">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">{t('provider.salesRep.createOffer')}</h2>
                <p className="text-gray-500">For {selectedLead.patient_name || selectedLead.patientName} - {selectedLead.treatment_interest || selectedLead.treatment}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setShowOfferComposer(false); setSelectedLead(null); setExistingOfferForCompose(null); }}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <OfferComposer
                lead={{
                  id: selectedLead.id,
                  treatment_interest: selectedLead.treatment_interest || selectedLead.treatment,
                  patient_id: selectedLead.patient_id || selectedLead.patientId,
                  // SSO-D — carry the patient's ACTUAL selection into the
                  // composer so the rep quotes against it, not blind.
                  campaign_attribution: selectedLead.campaign_attribution || null,
                  preferred_doctor: selectedLead.preferred_doctor || null,
                  selection_events: selectedLead.selection_events || null,
                }}
                existingOffer={existingOfferForCompose}
                highestVersion={Math.max(
                  0,
                  ...(offerVersions || []).map(
                    (o) => Number(o.version_number ?? o.version ?? 0) || 0))}
                onOfferSent={handleOfferSent}
                onCancel={() => { setShowOfferComposer(false); setSelectedLead(null); setExistingOfferForCompose(null); }}
                /* FASTFIX-OFFER-COMPOSER-FEATURE-PARITY-01 — lift the lean
                   modal's capabilities into the canonical Offer UI. */
                enableTemplates
                enableCampaigns
                showPatientContext
                leadId={selectedLead.id}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* SPRINT-3: Add Own Patient Modal */}
      <AddLeadModal
        isOpen={showAddPatientModal}
        onClose={() => setShowAddPatientModal(false)}
        onCreated={handleCreateOwnPatient}
        clinicId={clinicId}
        allowInvite
      />

      {/* BULK-LEAD-IMPORT-V1 — staged import workspace (Sales Rep: self-assignment only) */}
      <BulkLeadImport
        isOpen={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onImported={() => { if (typeof loadData === 'function') loadData(); }}
      />
      
      {/* LIFECYCLE-V3.2 — Mark as Won (explicit business action) */}
      {markWonLead && (
        <MarkAsWonDialog lead={markWonLead} open={!!markWonLead} onClose={() => setMarkWonLead(null)} onDone={() => loadData && loadData()} />
      )}
      
      {/* P3: Build stamp for deployment verification */}
    </div>
  );
};

// Wrapper with SubscriptionProvider — ALWAYS renders, never blocks on sessionStorage
const SalesRepDashboardWithSubscription = () => {
  const { t } = useTranslation();
  const { clinicUser } = useClinicData();
  const { clinicId: rbacClinicId } = useClinicScope();
  const { isAuthenticated } = useRBAC();
  const resolvedClinicId = rbacClinicId || clinicUser?.clinicId;
  
  // If we have a clinicId (from RBAC localStorage or clinicUser sessionStorage), wrap with subscription
  if (resolvedClinicId) {
    return (
      <SubscriptionProvider clinicId={resolvedClinicId}>
        <SalesRepDashboard />
      </SubscriptionProvider>
    );
  }

  // RBAC is authenticated but clinicId not yet resolved — show loading (ClinicDataContext will hydrate)
  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="sales-dashboard-loading-wrapper">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">{t('provider.salesRep.resolvingContext')}</p>
        </div>
      </div>
    );
  }

  // Not authenticated — render without provider; inner component will show loading/redirect
  return <SalesRepDashboard />;
};

export default SalesRepDashboardWithSubscription;
